/**
 * BigFit Theme — theme.js
 * Handles: scroll-reveal, parallax hero, page transitions, header scroll, mobile menu
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────────
     Page Transition (disabled)
  ──────────────────────────────────────────── */
  function pageEnter() {}

  function pageLeave(callback) { callback(); }

  // Fade out on internal link click
  document.addEventListener('click', function (e) {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    const isInternal = (
      href &&
      !href.startsWith('#') &&
      !href.startsWith('mailto:') &&
      !href.startsWith('tel:') &&
      !anchor.hasAttribute('target') &&
      anchor.hostname === window.location.hostname
    );

    if (isInternal) {
      e.preventDefault();
      pageLeave(() => {
        window.location.href = href;
      });
    }
  });

  /* ────────────────────────────────────────────
     Scroll-Reveal (IntersectionObserver)
  ──────────────────────────────────────────── */
  const revealEls = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');

  if ('IntersectionObserver' in window && revealEls.length) {
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    revealEls.forEach(function (el) {
      observer.observe(el);
    });
  } else {
    // Fallback: show all immediately
    revealEls.forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  /* ────────────────────────────────────────────
     Header: transparent → solid on scroll
  ──────────────────────────────────────────── */
  const header = document.getElementById('site-header');

  function updateHeader() {
    if (!header) return;
    if (window.scrollY > 60) {
      header.classList.remove('is-transparent');
      header.classList.add('is-solid');
    } else {
      header.classList.remove('is-solid');
      header.classList.add('is-transparent');
    }
  }

  if (header) {
    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();
  }

  /* ────────────────────────────────────────────
     Parallax Hero
  ──────────────────────────────────────────── */
  const heroBg = document.getElementById('hero-parallax');

  function updateParallax() {
    if (!heroBg) return;
    const scrolled = window.scrollY;
    const rate = scrolled * 0.35;
    heroBg.style.transform = 'translateY(' + rate + 'px) scale(1.08)';
  }

  if (heroBg && window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
    window.addEventListener('scroll', updateParallax, { passive: true });
  }

  /* ────────────────────────────────────────────
     Mobile Menu
  ──────────────────────────────────────────── */
  const menuToggle  = document.getElementById('mobile-menu-toggle');
  const mobileNav   = document.getElementById('mobile-nav');
  const navClose    = document.getElementById('mobile-nav-close');

  function openMobileNav() {
    if (!mobileNav) return;
    mobileNav.classList.add('is-open');
    mobileNav.setAttribute('aria-hidden', 'false');
    menuToggle.setAttribute('aria-expanded', 'true');
    menuToggle.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  function closeMobileNav() {
    if (!mobileNav) return;
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    if (menuToggle) {
      menuToggle.setAttribute('aria-expanded', 'false');
      menuToggle.classList.remove('is-active');
    }
    document.body.style.overflow = '';
  }

  if (menuToggle)  menuToggle.addEventListener('click', openMobileNav);
  if (navClose)    navClose.addEventListener('click', closeMobileNav);

  // Close on Escape
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeMobileNav();
  });

  // Close on mobile nav link click
  if (mobileNav) {
    mobileNav.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', closeMobileNav);
    });
  }

  /* ────────────────────────────────────────────
     FAQ Accordion
  ──────────────────────────────────────────── */
  document.querySelectorAll('.faq-question').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const item   = btn.closest('.faq-item');
      const isOpen = item.classList.contains('is-open');

      // Close all
      document.querySelectorAll('.faq-item.is-open').forEach(function (openItem) {
        openItem.classList.remove('is-open');
        openItem.querySelector('.faq-question').setAttribute('aria-expanded', 'false');
      });

      // Open clicked (unless it was already open)
      if (!isOpen) {
        item.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* ────────────────────────────────────────────
     Quick-Add to Cart (product card buttons)
  ──────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.product-card__add-btn');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.innerHTML = '<span class="spinner"></span>';

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        return fetch('/cart.js');
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        openCartDrawer();
        btn.classList.remove('is-loading');
        btn.textContent = 'Add to Cart';
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.textContent = 'Add to Cart';
      });
  });

  /* ────────────────────────────────────────────
     Cart Count Helpers (shared with cart-drawer.js)
  ──────────────────────────────────────────── */
  window.updateCartCount = function (count) {
    const el = document.getElementById('cart-count');
    if (!el) return;
    el.textContent = count;
    el.dataset.count = count;
    el.style.display = count > 0 ? 'flex' : 'none';
    el.classList.add('bump');
    setTimeout(function () { el.classList.remove('bump'); }, 400);
  };

  window.openCartDrawer = function () {
    const drawer  = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-overlay');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    const cartToggle = document.getElementById('cart-drawer-toggle');
    if (cartToggle) cartToggle.setAttribute('aria-expanded', 'true');
  };

  window.closeCartDrawer = function () {
    const drawer  = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-overlay');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    const cartToggle = document.getElementById('cart-drawer-toggle');
    if (cartToggle) cartToggle.setAttribute('aria-expanded', 'false');
  };

  // Cart drawer toggle button
  const cartToggle = document.getElementById('cart-drawer-toggle');
  if (cartToggle) cartToggle.addEventListener('click', window.openCartDrawer);

  const cartClose = document.getElementById('cart-drawer-close');
  if (cartClose) cartClose.addEventListener('click', window.closeCartDrawer);

  const cartOverlay = document.getElementById('cart-overlay');
  if (cartOverlay) cartOverlay.addEventListener('click', window.closeCartDrawer);

})();
